// TelcoTrack — compact Od–Do (or single date) filter behind a calendar icon.

import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  claimFilterOverlay,
  releaseFilterOverlay,
} from '../../../utils/filter-overlay-coordinator.util';
import { filterOverlayPanelStyle } from '../../../utils/filter-overlay-position.util';

@Component({
  selector: 'app-filter-date-range',
  templateUrl: './filter-date-range.component.html',
  styleUrls: ['./filter-date-range.component.scss'],
})
export class FilterDateRangeComponent implements OnDestroy {
  @Input() idPrefix = 'filter-date';
  @Input() mode: 'range' | 'single' = 'range';
  @Input() dateFrom: string | null = '';
  @Input() dateTo: string | null = '';
  @Input() fromLabel = 'Od';
  @Input() toLabel = 'Do';
  @Input() singleLabel = 'Datum';
  @Input() inputType: 'text' | 'date' | 'week' | 'month' = 'text';
  @Input() placeholder = 'dd.mm.yyyy';
  @Input() panelTitle = 'Období';
  @Input() disabled = false;

  @Output() dateFromChange = new EventEmitter<string>();
  @Output() dateToChange = new EventEmitter<string>();
  @Output() rangeBlur = new EventEmitter<void>();

  @ViewChild('triggerBtn') private triggerBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelEl') private panelEl?: ElementRef<HTMLElement>;

  panelOpen = false;
  panelStyle: { [key: string]: string } = {};

  private panelInBody = false;
  private panelOriginalParent: Node | null = null;
  private readonly reposition = (): void => this.updatePanelPosition();

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  get hasValue(): boolean {
    return !!(this.dateFrom || this.dateTo);
  }

  get triggerTitle(): string {
    if (this.mode === 'single') {
      return this.dateFrom ? `${this.singleLabel}: ${this.dateFrom}` : this.singleLabel;
    }
    if (this.dateFrom || this.dateTo) {
      return `${this.fromLabel} ${this.dateFrom || '…'} — ${this.toLabel} ${this.dateTo || '…'}`;
    }
    return this.panelTitle;
  }

  togglePanel(): void {
    if (this.disabled) return;
    if (this.panelOpen) {
      this.closePanel();
      return;
    }
    claimFilterOverlay(this);
    this.panelOpen = true;
    this.updatePanelPosition();
    requestAnimationFrame(() => {
      this.movePanelToBody();
      this.updatePanelPosition();
    });
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  closePanel(): void {
    if (!this.panelOpen) return;
    this.restorePanelToHost();
    this.panelOpen = false;
    releaseFilterOverlay(this);
    this.teardownReposition();
  }

  onFromInput(value: string): void {
    this.dateFromChange.emit(value);
  }

  onToInput(value: string): void {
    this.dateToChange.emit(value);
  }

  onBlur(): void {
    this.rangeBlur.emit();
  }

  onPanelPointerDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  clearValues(): void {
    this.dateFromChange.emit('');
    if (this.mode === 'range') this.dateToChange.emit('');
    this.rangeBlur.emit();
    this.closePanel();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.panelOpen) return;
    const target = event.target as Node;
    const insideHost = this.host.nativeElement.contains(target);
    const insidePanel = this.panelEl?.nativeElement.contains(target) ?? false;
    if (!insideHost && !insidePanel) {
      this.closePanel();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closePanel();
  }

  ngOnDestroy(): void {
    this.teardownReposition();
    releaseFilterOverlay(this);
    if (this.panelInBody) {
      this.panelEl?.nativeElement.remove();
      this.panelInBody = false;
      this.panelOriginalParent = null;
    }
  }

  private movePanelToBody(): void {
    const panel = this.panelEl?.nativeElement;
    if (!panel || this.panelInBody) return;
    this.panelOriginalParent = panel.parentNode;
    document.body.appendChild(panel);
    this.panelInBody = true;
  }

  private restorePanelToHost(): void {
    if (!this.panelInBody) return;
    const panel = this.panelEl?.nativeElement;
    if (panel && this.panelOriginalParent) {
      this.panelOriginalParent.appendChild(panel);
    }
    this.panelInBody = false;
    this.panelOriginalParent = null;
  }

  private updatePanelPosition(): void {
    const btn = this.triggerBtn?.nativeElement;
    if (!btn) return;
    this.panelStyle = filterOverlayPanelStyle(btn, this.panelEl?.nativeElement, 288);
  }

  private teardownReposition(): void {
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
  }
}
